import type { ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { Plus, History, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConversationMoreMenu } from './conversation-more-menu';
import type { ChatFeedbackCategory } from '@/lib/chat-feedback';
import { useChatSurfaceRegistry } from '@/hooks/use-chat-surface-registry';
import { testIds } from '@/lib/test-ids';
import { ShellPanelToggle } from '@/components/layout/shell';
import { useShellChrome } from '@/components/layout/resizable-layout.flyout';
import { SHELL_COLUMN_HEADER_CLASS } from '@/lib/shell-chrome';
import { cn } from '@/lib/utils';

interface ActiveConversation {
  id: string;
  title: string;
}

interface ConversationHeaderProps {
  activeConversations?: ActiveConversation[];
  currentConversationId?: string;
  /** Registry thread id (includes new-* drafts) so move menu works before first message. */
  hostedThreadId?: string | null;
  /** Thread title from GET /api/threads (same source as chats sidebar). */
  hostedThreadTitle?: string;
  /** Agent panel surface (compact) always gets move menu; persisted chats and new-* drafts too. */
  showSurfaceMenu?: boolean;
  onNewChat?: () => void;
  onShowHistory?: () => void;
  onSelectConversation?: (id: string) => void;
  onCloseConversation?: (id: string) => void;
  onExport?: () => void;
  onCopyTranscript?: () => void;
  onCopyConversationId?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onClearHistory?: () => void;
  onSettings?: () => void;
  onMoveToEditorTab?: () => void;
  onFeedback?: (category?: ChatFeedbackCategory) => void;
  hasChatMessages?: boolean;
  /** Omit bulk clear-history in agent panel (editor toolbar keeps it). */
  showClearHistory?: boolean;
  /** Optional ref for scroll container (e.g. demo / future sticky header behavior) */
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  /** Optional slot for scope badge (e.g. ChatScopeBadge) */
  scopeBadge?: ReactNode;
  /** Warm path: propose durable facts to soul via inbox */
  onLearnFromSession?: () => void;
  sessionFilesCount?: number;
  sessionFilesOpen?: boolean;
  onSessionFilesOpenChange?: (open: boolean) => void;
  /** Optional flyout under the title row (same sticky chrome, one hairline). */
  sessionFilesSlot?: ReactNode;
  /** Agent column docked. The Bot toggle is pressed while this is false. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function isGenericThreadTitle(title: string | null | undefined): boolean {
  if (!title) return true;
  const t = title.trim().toLowerCase();
  return t === '' || t === 'chat' || t === 'new chat' || t === 'new conversation';
}

export function ConversationHeader({
  activeConversations,
  currentConversationId,
  hostedThreadId,
  hostedThreadTitle,
  scrollContainerRef: _scrollContainerRef,
  onNewChat,
  onShowHistory,
  onSelectConversation: _onSelectConversation,
  onCloseConversation: _onCloseConversation,
  onExport,
  onCopyTranscript,
  onCopyConversationId,
  onRename,
  onDelete,
  onClearHistory,
  onSettings,
  onMoveToEditorTab,
  onFeedback,
  hasChatMessages = false,
  showClearHistory = false,
  showSurfaceMenu = false,
  scopeBadge,
  onLearnFromSession,
  sessionFilesCount = 0,
  sessionFilesOpen = false,
  onSessionFilesOpenChange,
  sessionFilesSlot,
  collapsed = false,
  onToggleCollapse,
}: ConversationHeaderProps) {
  const { agentDisabledReason } = useShellChrome();
  const registry = useChatSurfaceRegistry();
  const displayConversations = activeConversations || [];
  const currentConversation = displayConversations.find((c) => c.id === currentConversationId);
  const showMoreMenu = Boolean(showSurfaceMenu || currentConversationId || hostedThreadId);

  const headerTitle = useMemo(() => {
    if (hostedThreadId?.startsWith('new-')) return 'New chat';

    if (hostedThreadId) {
      const fromRegistry = registry?.resolveTitle(hostedThreadId);
      if (!isGenericThreadTitle(fromRegistry)) return fromRegistry!.trim();
      if (!isGenericThreadTitle(hostedThreadTitle)) return hostedThreadTitle!.trim();
    }

    if (currentConversationId && currentConversation?.title) {
      return currentConversation.title;
    }

    return 'AI Assistant';
  }, [
    hostedThreadId,
    hostedThreadTitle,
    registry,
    currentConversationId,
    currentConversation?.title,
  ]);

  return (
    <div className="sticky top-0 z-10 bg-background">
      <div className={cn('flex items-center gap-2 border-b border-border px-3', SHELL_COLUMN_HEADER_CLASS)}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h3 className="min-w-0 truncate text-[13px] font-medium leading-none tracking-tight text-foreground">
            {headerTitle}
          </h3>
          {scopeBadge != null ? <div className="shrink-0">{scopeBadge}</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {onFeedback ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => onFeedback(hasChatMessages ? 'agent-issue' : 'feedback')}
              title="Report issue or send feedback"
              aria-label="Report issue or send feedback"
              data-testid={testIds.chatPane.feedbackButton}
            >
              <Flag className="h-3.5 w-3.5" />
            </Button>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onShowHistory}
            title="Conversation History (⌘H)"
          >
            <History className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={onNewChat}
            title="New chat"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>

          {showMoreMenu ? (
            <ConversationMoreMenu
              conversationId={currentConversationId}
              onExport={onExport}
              onCopyTranscript={onCopyTranscript}
              onCopyConversationId={onCopyConversationId}
              onRename={onRename}
              onDelete={onDelete}
              onClearHistory={onClearHistory}
              onSettings={onSettings}
              onMoveToEditorTab={onMoveToEditorTab}
              onFeedback={onFeedback}
              onLearnFromSession={onLearnFromSession}
              hasChatMessages={hasChatMessages}
              showClearHistory={showClearHistory}
              sessionFilesCount={sessionFilesCount}
              sessionFilesOpen={sessionFilesOpen}
              onSessionFilesOpenChange={onSessionFilesOpenChange}
            />
          ) : null}

          {onToggleCollapse ? (
            <ShellPanelToggle
              side="agent"
              placement="inline"
              open={!collapsed}
              disabledReason={collapsed ? agentDisabledReason : undefined}
              onToggle={onToggleCollapse}
            />
          ) : null}
        </div>
      </div>
      {sessionFilesSlot}
    </div>
  );
}
